import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { logEvent } from "../analytics";

const DRAFT_KEY = "prepnexis_onboarding_draft";

export const ONBOARDING_QUESTIONS = [
  {
    key: "target_score",
    heading: "What PTE score are you aiming for?",
    options: [
      ["50_plus", "50+"],
      ["58_plus", "58+"],
      ["65_plus", "65+"],
      ["79_plus", "79+"],
      ["85_plus", "85+"],
      ["not_sure", "Not sure yet"],
    ],
  },
  {
    key: "exam_timeline",
    heading: "When is your PTE exam?",
    options: [
      ["within_7_days", "Within 7 days"],
      ["1_2_weeks", "1–2 weeks"],
      ["2_4_weeks", "2–4 weeks"],
      ["more_than_1_month", "More than 1 month away"],
      ["not_booked", "I haven’t booked my exam yet"],
    ],
  },
  {
    key: "pte_goal",
    heading: "What is your main goal for taking the PTE?",
    options: [
      ["study_abroad", "Study abroad / University admission"],
      ["permanent_residency", "Permanent Residency (PR)"],
      ["work_professional", "Work / Professional registration"],
      ["visa_immigration", "Visa / Immigration requirement"],
      ["personal_academic", "Personal or academic improvement"],
      ["other", "Other"],
    ],
  },
  {
    key: "acquisition_source",
    heading: "How did you hear about PrepNexis?",
    options: [
      ["google", "Google Search"],
      ["youtube", "YouTube"],
      ["instagram", "Instagram"],
      ["tiktok", "TikTok"],
      ["reddit", "Reddit"],
      ["facebook", "Facebook"],
      ["pte_tutor", "PTE tutor / Coaching centre"],
      ["friend_family", "Friend or family"],
      ["study_abroad_consultant", "Study-abroad consultant"],
      ["immigration_consultant", "Immigration / Migration consultant"],
      ["other", "Other"],
    ],
  },
];

const readDraft = () => {
  try {
    const draft = JSON.parse(window.sessionStorage.getItem(DRAFT_KEY));
    return draft && typeof draft === "object" ? draft : {};
  } catch {
    return {};
  }
};

const clearDraft = () => {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // Session storage is optional; the server remains the source of truth.
  }
};

const Onboarding = ({ embedded = false, onFinished }) => {
  const navigate = useNavigate();
  const startedEventSent = useRef(false);
  const initialDraft = useMemo(readDraft, []);
  const [step, setStep] = useState(() => {
    const value = Number(initialDraft.step);
    return Number.isInteger(value) && value >= 0 && value < ONBOARDING_QUESTIONS.length ? value : 0;
  });
  const [answers, setAnswers] = useState(initialDraft.answers || {});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const question = ONBOARDING_QUESTIONS[step];
  const progress = Math.round(((step + 1) / ONBOARDING_QUESTIONS.length) * 100);

  const loadOnboarding = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/onboarding", { credentials: "include", cache: "no-store" });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "We couldn't load onboarding.");
      if (!data.required) {
        clearDraft();
        if (onFinished) onFinished();
        else navigate("/dashboard", { replace: true });
        return;
      }

      const savedAnswers = Object.fromEntries(
        Object.entries(data.responses || {}).filter(([, value]) => Boolean(value))
      );
      setAnswers((current) => ({ ...savedAnswers, ...current }));
      if (!startedEventSent.current) {
        startedEventSent.current = true;
        logEvent("onboarding_started");
      }
    } catch (requestError) {
      setError(requestError.message || "We couldn't load onboarding. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [navigate, onFinished]);

  useEffect(() => {
    loadOnboarding();
  }, [loadOnboarding]);

  useEffect(() => {
    if (loading) return;
    try {
      window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ step, answers }));
    } catch {
      // Refresh recovery is best-effort only.
    }
  }, [answers, loading, step]);

  const selectAnswer = (answer) => {
    setError("");
    setAnswers((current) => ({ ...current, [question.key]: answer }));
    logEvent("onboarding_question_answered", {
      question: question.key,
      answer,
      step: step + 1,
    });
  };

  const finish = async (payload, eventName, eventProperties) => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/onboarding", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "We couldn't save your answers.");

      clearDraft();
      logEvent(eventName, eventProperties);
      if (onFinished) onFinished();
      else navigate("/dashboard", { replace: true });
    } catch (requestError) {
      setError(requestError.message || "We couldn't save your answers. Please try again.");
      setSaving(false);
    }
  };

  const continueOnboarding = () => {
    if (!answers[question.key]) return;
    if (step < ONBOARDING_QUESTIONS.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    finish(
      { action: "complete", responses: answers },
      "onboarding_completed",
      answers
    );
  };

  const skipOnboarding = () =>
    finish(
      { action: "skip", stepSkippedAt: step + 1 },
      "onboarding_skipped",
      { step_skipped_at: step + 1 }
    );

  if (loading) {
    const loadingContent = (
        <div className="flex items-center gap-3 text-sm font-medium text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin text-fuchsia-800" aria-hidden="true" />
          Preparing your study plan…
        </div>
    );
    if (embedded) return <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-50 px-4" aria-busy="true">{loadingContent}</div>;
    return <main className="grid min-h-screen place-items-center bg-slate-50 px-4" aria-busy="true">{loadingContent}</main>;
  }

  if (error && !startedEventSent.current) {
    const errorContent = (
        <section className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-sm text-red-700" role="alert">{error}</p>
          <button type="button" onClick={loadOnboarding} className="mt-5 rounded-md bg-fuchsia-900 px-5 py-3 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:ring-offset-2">
            Try again
          </button>
          <button
            type="button"
            onClick={skipOnboarding}
            disabled={saving}
            className="mt-3 block w-full rounded-md px-4 py-2 text-sm font-semibold text-slate-500 underline-offset-4 hover:text-fuchsia-900 hover:underline focus:outline-none focus:ring-2 focus:ring-fuchsia-400 disabled:opacity-50"
          >
            {saving ? "Skipping…" : "Skip for now"}
          </button>
        </section>
    );
    if (embedded) return <div className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-slate-50 px-4 py-6">{errorContent}</div>;
    return <main className="grid min-h-screen place-items-center bg-slate-50 px-4">{errorContent}</main>;
  }

  const onboardingContent = (
      <section className={`mx-auto flex w-full max-w-2xl flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8 md:p-10 ${embedded ? "min-h-[min(680px,calc(100vh-3rem))]" : "min-h-[calc(100vh-3rem)] sm:min-h-0"}`}>
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={skipOnboarding}
            disabled={saving}
            className="rounded-md px-2 py-2 text-sm font-semibold text-slate-500 underline-offset-4 hover:text-fuchsia-900 hover:underline focus:outline-none focus:ring-2 focus:ring-fuchsia-400 disabled:opacity-50"
          >
            Skip for now
          </button>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
            <span>Step {step + 1} of {ONBOARDING_QUESTIONS.length}</span>
            <span>{progress}%</span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100" aria-label={`${progress}% complete`} role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress}>
            <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex-1">
          <h1 className="mt-8 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {question.heading}
          </h1>
          <p className="mt-2 text-sm text-slate-500">Choose the answer that fits you best.</p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2" role="group" aria-label={question.heading}>
            {question.options.map(([value, label]) => {
              const selected = answers[question.key] === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => selectAnswer(value)}
                  className={`flex min-h-14 items-center justify-between rounded-lg border px-4 py-3 text-left text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:ring-offset-2 ${
                    selected
                      ? "border-fuchsia-800 bg-fuchsia-50 text-fuchsia-950 shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-fuchsia-300 hover:bg-fuchsia-50/50"
                  }`}
                >
                  <span>{label}</span>
                  {selected && <Check className="ml-3 h-5 w-5 shrink-0 text-fuchsia-800" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>

        {error && <p className="mt-5 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>}

        <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            disabled={step === 0 || saving}
            className="inline-flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
          <button
            type="button"
            onClick={continueOnboarding}
            disabled={!answers[question.key] || saving}
            className="inline-flex min-h-11 min-w-32 items-center justify-center gap-2 rounded-md bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:ring-2 hover:ring-fuchsia-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {saving ? "Saving…" : step === ONBOARDING_QUESTIONS.length - 1 ? "Finish" : "Continue"}
          </button>
        </div>
      </section>
  );
  if (embedded) {
    return (
      <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-50 px-4 py-6 sm:px-6 sm:py-10">
        <div className="flex min-h-full items-center justify-center">
          {onboardingContent}
        </div>
      </div>
    );
  }
  return <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 sm:py-10">{onboardingContent}</main>;
};

export default Onboarding;
